<template>
    <div style="flex-grow: 1;">
        <div
            v-if="isRecurring"
            class="caption font-weight-medium n4--text transition-ease-in-out"
            :class="isFullPage ? 'pl-6' : 'pl-3'"
            :style="fieldFocused ? '' : 'transform: translateY(50%)'"
        >
            {{ $t("Recurrence task") }}
        </div>
        <div class="d-flex">
            <div :class="{ 'px-3': isFullPage }" style="max-width: 512px; width: 100%">
                <v-combobox
                    v-if="showAutocomplete && isTemplateAutocomplete"
                    ref="titleField"
                    :value="value"
                    outlined
                    dense
                    hide-selected
                    class="pt-0 title task-title"
                    :placeholder="$t('Search from one time templates')"
                    :error-messages="errorMessage"
                    :items="nonRecurringTemplateTasks"
                    :menu-props="{
                        maxWidth: 512,
                        maxHeight: 280
                    }"
                    append-icon=""
                    hide-details="auto"
                    item-value="name"
                    test-id="task-title"
                    @update:search-input="updateTaskTitle"
                    @change="handleNonRecurringAutocompleteSelect"
                    @focusin="fieldFocused = true"
                    @focusout="fieldFocused = false; isTemplateAutocomplete = false"
                >
                    <template v-slot:item="{ item }">
                        <v-list-item-icon>
                            <icon
                                icon="FileText"
                                color="n3"
                            />
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title class="body-2">
                                <Highlight
                                    :search="value"
                                    :text="item.title"
                                />
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action class="my-2">
                            <v-list-item-action-text class="body-2">
                                <Highlight
                                    :search="value"
                                    :text="item.client.name"
                                />
                            </v-list-item-action-text>
                        </v-list-item-action>
                    </template>
                    <template #append>
                        <icon-button
                            icon="X"
                            color="n3"
                            @click="isTemplateAutocomplete = false"
                        />
                    </template>
                </v-combobox>
                <v-combobox
                    v-else-if="showAutocomplete"
                    ref="titleField"
                    :value="value"
                    outlined
                    dense
                    hide-selected
                    class="pt-0 title task-title"
                    :placeholder="$t('Task title')"
                    :error-messages="errorMessage"
                    :items="autocomplete_list"
                    :menu-props="{
                        maxWidth: 512
                    }"
                    append-icon=""
                    hide-details="auto"
                    item-value="name"
                    test-id="task-title"
                    @update:search-input="updateTaskTitle"
                    @change="handleAutocompleteSelect"
                    @focusin="fieldFocused = true"
                    @focusout="fieldFocused = false"
                >
                    <template v-slot:item="{ item }">
                        <ClientAvatarListItem
                            :color="item.color"
                            :client-initials="item.client_initial"
                            :size="24"
                            class-name="mr-2 my-0"
                        ></ClientAvatarListItem>
                        <v-list-item-content>
                            <v-list-item-title class="body-2">
                                <Highlight
                                    :search="value"
                                    :text="item.name"
                                />
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action class="my-2">
                            <v-list-item-action-text
                                v-if="item.client_name"
                                class="body-2"
                            >
                                {{ item.client_name | max_len(16) }}
                            </v-list-item-action-text>
                        </v-list-item-action>
                    </template>
                    <template #append>
                        <v-btn
                            v-if="!taskData.title && !isTaskButton"
                            text
                            class="px-3"
                            @click="onEnableTemplateAutocomplete"
                        >
                            <icon
                                color="n3"
                                icon="FileText"
                            />
                            <div class="body-2 mx-1 text-none n4--text">
                                {{ $t('Templates') }}
                            </div>
                            <icon
                                size="18"
                                color="n3"
                                icon="ChevronDown"
                            />
                        </v-btn>
                    </template>
                </v-combobox>
                <v-textarea
                    v-else
                    ref="titleField"
                    :value="value"
                    outlined
                    auto-grow
                    rows="1"
                    class="pt-0 title task-title"
                    :placeholder="$t('Task title')"
                    hide-details="auto"
                    :error-messages="errorMessage"
                    test-id="task-title"
                    @input="setTitleValue"
                    @blur="onBlur"
                    @keydown.enter.prevent="$emit('focus-next', 'title')"
                    @focusin="focusIn()"
                    @focusout="focusOut()"
                    @click="setTitleCursorInfo()"
                    @keyup="setTitleCursorInfo()"
                >
                    <template #append>
                        <div v-show="isPlaceholderOptionVisible" >
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <div v-on="on">
                                        <RichTextEditorPlaceholders
                                            :company-id="companyId"
                                            :placeholder-types="placeholderTypes"
                                            :rich-text-editor="null"
                                            :open-menu-right="false"
                                            @placeholder="addPlaceholder"
                                        >
                                            <template #activator="{ on }">
                                                <v-btn
                                                    v-on="on"
                                                    text
                                                    class="px-3"
                                                    @click="placeholderOptionClicked(true)"
                                                    @blur="onPlaceholderMenuBlur"
                                                >
                                                    <icon
                                                        color="n3"
                                                        icon="CurlyBraces"
                                                    />
                                                    <div class="body-2 mx-1 text-none n4--text">
                                                        {{ $t('Placeholders') }}
                                                    </div>
                                                    <icon
                                                        size="18"
                                                        color="n3"
                                                        icon="ChevronDown"
                                                    />
                                                </v-btn>
                                            </template>
                                        </RichTextEditorPlaceholders>
                                    </div>
                                </template>
                                <span>{{ $t("Add placeholder") }}</span>
                            </v-tooltip>
                        </div>
                    </template>
                </v-textarea>
            </div>
            <TaskHeaderButtons
                :is-task-button="isTaskButton"
                :is-full-page="isFullPage"
                :task-data="taskData"
                :auto-save="autoSave"
                :recurrence-rule-field="recurrenceRuleField"
                :menu-items="menuItems"
                :estimations_menu="estimationsMenu"
                :is-recurring="isRecurring"
                @close_click="$emit('close_click', $event)"
                @focus_field="$emit('focus_field', $event)"
                @toggle_field="$emit('toggle_field', $event)"
                @action_click="$emit('action_click', $event)"
            />
        </div>
        <div
            class="d-flex align-center row pl-6 mt-2 body-2 transition-ease-in-out"
            :class="{'pl-6': !$vuetify.breakpoint.mdAndUp}"
            :style="fieldFocused ? '' : 'transform: translateY(' + (taskData.is_cp ? '-8px' : '-10px') + ')'"
        >
            <div
                v-if="taskData.is_cp"
                class="d-flex align-center justify-center rounded-circle custom-icon"
            >
                <icon
                    size="12"
                    icon="Users"
                    color="white"
                />
            </div>
            <v-chip
                v-if="taskData.is_cp && taskData.origin === 'cp'"
                class="caption ml-1 mr-2 tretiary--text tretiary lighten-2"
            >
                {{ $t("Client request").toUpperCase() }}
            </v-chip>
            <v-chip
                v-else-if="taskData.is_cp"
                class="caption ml-1 mr-2 royal-blue--text royal-blue lighten-2"
            >
                {{ $t("Client portal").toUpperCase() }}
            </v-chip>
            <div
                v-if="allErrors.length > 0"
                class="error--text body-2"
                :class="{'pl-6': !$vuetify.breakpoint.mdAndUp}"
            >
                {{ allErrors.join(" | ") }}
            </div>
        </div>
    </div>
</template>

<script>
import ApiTask from "@/api/task/task"
import {debounce} from "throttle-debounce";
import ClientAvatarListItem from "@/components/ClientAvatarListItem.vue";
import {ref, watch, computed} from "@vue/composition-api";
import {formatOverdue, formatSysDate} from "../../../plugins/formatting";
import TaskHeaderButtons from "@/views/Task/TaskFields/TaskHeaderButtons";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import Highlight from "@/components/Highlight.vue";
import TaskModule from "@/store/modules/task";
import RichTextEditorPlaceholders from "../../../components/RichTextEditor/RichTextEditorPlaceholders.vue";
import ApiEmailAutomationPlaceholders from "../../../api/email_automation/placeholders";

export default {
    components: {
        RichTextEditorPlaceholders,
        TaskHeaderButtons,
        ClientAvatarListItem,
        Highlight
    },
    props: {
        showAutocomplete: {
            type: Boolean,
            default: true
        },
        errorMessage: {
            type: String,
            default: ''
        },
        value: {
            type: String,
            default: ''
        },
        isFullPage: {
            type: Boolean,
            default: false
        },
        showOverdue: {
            type: Boolean,
            default: false
        },
        overdueSeconds: {
            type: Number,
            default: 0
        },
        autofocusField: {
            type: String,
            default: ''
        },
        taskErrors: {
            type: Array,
            default: () => []
        },
        isRecurring: {
            type: Boolean,
            default: false
        },
        taskData: {
            type: Object,
            default: null
        },
        isTaskButton: {
            type: Boolean,
            default: false
        },
        autoSave: {
            type: [Boolean, Number],
            default: false
        },
        recurrenceRuleField: {
            type: Object,
            default: null
        },
        menuItems: {
            type: Array,
            default: null
        },
        estimationsMenu: {
            type: Array,
            default: null
        },
        clients: {
            type: Array,
            default: null
        },
        companyId: {
            type: Number,
            default: 0
        }
    },
    setup(props, ctx) {
        const {
            autoSuggestTasks,
        } = TaskModule();
        const textValue = ref(props.value);
        const titleField = ref(null);
        const fieldFocused = ref(false);
        const isTemplateAutocomplete = ref(false);
        const nonRecurringTemplateTasks = ref([]);
        const autocomplete_list = ref([]);
        const titleCursorIndex = ref(0)
        const reFocusTitle = ref(false)
        const showPlaceholdersOption = ref(false)
        const placeholderOptionTriggered = ref(false)
        const {getPlaceholderTypes} = ApiEmailAutomationPlaceholders

        const placeholderTypes = computed(() => {
            return getPlaceholderTypes("task_title")
        })

        const debouncedGetNonRecurringTemplateTasks = debounce(300, false, async () => {
            try {
                const data = await ApiTask.getNonRecurringTemplateTasks(textValue.value)
                nonRecurringTemplateTasks.value = data.tasks
            } catch (e) {
                errorDialog.showError(e)
            }
        });

        const debouncedGetAutoSuggestTasks = debounce(300, false, async () => {
            try {
                if (!props.value) {
                    return
                }

                const resp = await autoSuggestTasks({ keyword: props.value, client_id: props.taskData.client_id || null })

                if (props.isTaskButton) {
                    autocomplete_list.value = resp.results.filter(result => {
                        return props.clients.some(client => {
                            return result.client_id === client.id;
                        });
                    })
                } else {
                    autocomplete_list.value = resp.results
                }
            } catch (e) {
                errorDialog.showError(e)
            }
        });

        const allErrors = computed(() => {
            let ret = [];
            if (props.showOverdue)
                ret.push(formatOverdue(props.overdueSeconds));
            for (let err of props.taskErrors) {
                ret.push(err);
            }
            return ret;
        });

        const updateTaskTitle = input => {
            let res = ''
            if (input !== null && typeof input === "object") {
                res = input.name;
            } else {
                res = input;
            }

            if (isTemplateAutocomplete.value) {
                debouncedGetNonRecurringTemplateTasks()
            } else {
                debouncedGetAutoSuggestTasks()
            }

            ctx.emit('input', res)
        };

        const handleNonRecurringAutocompleteSelect = function(task){
            let taskCopy = {...task}
            taskCopy.client_id = 0
            taskCopy.client = null
            taskCopy.name = taskCopy.title
            isTemplateAutocomplete.value = false

            ctx.emit('autocomplete-select', taskCopy)
        }

        const handleAutocompleteSelect = function(item){
            ctx.emit('autocomplete-select', item)
        }

        watch (
            () => props.value,
            () => {
                textValue.value = props.value
            }
        )

        const watchAutoFocusField = computed(function() {
            return props.autofocusField;
        });

        const onEnableTemplateAutocomplete = () => {
            isTemplateAutocomplete.value = true
            debouncedGetNonRecurringTemplateTasks()
        }

        const isPlaceholderOptionVisible = computed(() => {
            return showPlaceholdersOption.value && props.isRecurring
        })

        const onBlur = () => {
            if (!placeholderOptionTriggered.value) {
                showPlaceholdersOption.value = false
            }

            if (textValue.value !== props.value) {
                ctx.emit('input', textValue.value)
                ctx.emit('save-field', 'title')
            }
        }

        const onPlaceholderMenuBlur = () => {
            // Blur if addPlaceholder is not running and the title won't be refocused
            setTimeout(() => {
                if (!reFocusTitle.value) {
                    placeholderOptionClicked(false)
                }
            }, 100)
        }

        const focusIn = () => {
            fieldFocused.value = true
            placeholderOptionTriggered.value = false
            showPlaceholdersOption.value = true
            reFocusTitle.value = false
        }

        const focusOut = () => {
            if (!placeholderOptionTriggered.value) {
                showPlaceholdersOption.value = false
            }
            fieldFocused.value = false
        }

        const setTitleCursorInfo = () => {
            const taskTitle = titleField.value.$refs.input;
            titleCursorIndex.value = taskTitle.selectionStart;
        }

        const setTitleValue = (event) => {
            textValue.value = event
        }

        const addPlaceholder = (placeholder) => {
            reFocusTitle.value = true
            if (!textValue.value) {
                textValue.value = ""
            }
            const firstPart = textValue.value.slice(0, titleCursorIndex.value);
            const secondPart = textValue.value.slice(titleCursorIndex.value);
            const newTitleValue = firstPart + placeholder.value + secondPart
            const newCursorIndex = titleCursorIndex.value + placeholder.value.length
            titleCursorIndex.value = newCursorIndex
            setTitleValue(newTitleValue)
            // Emit a title change to display the automatically added placeholder in the title field
            ctx.emit('input', textValue.value)

            const inputElement = titleField.value.$refs.input
            setTimeout(() => {
                inputElement.focus()
                inputElement.setSelectionRange(newCursorIndex, newCursorIndex);
            }, 200);
        }

        const placeholderOptionClicked = (value) => {
            showPlaceholdersOption.value = value
            placeholderOptionTriggered.value = value
        }

        watch(watchAutoFocusField, function () {
            if (props.autofocusField === 'title') {
                setTimeout(() => {
                    if (titleField.value) {
                        titleField.value.focus()
                    }
                }, 500)
            }
        })

        return {
            updateTaskTitle,
            handleAutocompleteSelect,
            onBlur,
            allErrors,
            textValue,
            titleField,
            fieldFocused,
            isTemplateAutocomplete,
            nonRecurringTemplateTasks,
            handleNonRecurringAutocompleteSelect,
            onEnableTemplateAutocomplete,
            autocomplete_list,
            placeholderTypes,
            focusIn,
            setTitleCursorInfo,
            setTitleValue,
            addPlaceholder,
            showPlaceholdersOption,
            focusOut,
            placeholderOptionClicked,
            isPlaceholderOptionVisible,
            onPlaceholderMenuBlur,
        };
    }
};
</script>

<style scoped lang="scss">
.v-list-item__icon {
    margin-right: 12px !important;
}

.v-list-item__action {
    max-width: 50%;

    .v-list-item__action-text  {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.highlight {
    font-weight: 400;

    strong {
        font-weight: 600;
    }
}

.task-title {
    ::v-deep .v-input__append-inner {
        margin-top: 2px !important;
        margin-right: 2px !important;
    }

    ::v-deep .v-input__slot {
        margin-bottom: 0 !important;
    }

    ::v-deep .v-messages {
        margin-top: 4px !important;
    }
}

.v-chip.v-size--default {
    height: 24px;
    font-size: 12px;
}
.custom-icon {
    height: 24px;
    width: 24px;
    background-color: #5075F3;
}

</style>